import { useMemo } from "react";
import {
    FiUsers,
    FiCpu,
    FiCalendar,
    FiServer,
    FiClock,
    FiAlertCircle,
    FiFolder,
    FiActivity,
    FiDatabase,
    FiZap,
} from "react-icons/fi";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { getAll as getAllCustomers } from "../../api/customers";
import {
    useSystemHealth,
    useGridSyncStatus,
    useGridImpactCoverage,
    useTaskMonitoring,
} from "../../features/admin/hooks/useMonitoring";
import SyncHeatmap from "../../features/admin/components/SyncHeatmap";

interface DashboardCustomer {
    customer_id: string;
    customer_name: string;
    contact_email?: string | null;
    plan_type?: string;
    created_at?: string | null;
}

interface AdminStats {
    totalCustomers: number;
    totalProjects: number;
    activeCustomers: number;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string | null | undefined): string {
    if (!iso) return "never";
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 0) return "just now";
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

type StatusColor = "green" | "yellow" | "red" | "gray";

function statusColor(status: string): StatusColor {
    switch (status) {
        case "healthy": return "green";
        case "warning": case "degraded": return "yellow";
        case "down": case "stale": case "no_data": return "red";
        default: return "gray";
    }
}

const dotColorClasses: Record<StatusColor, string> = {
    green: "bg-quantum-500",
    yellow: "bg-amber-400",
    red: "bg-health-500",
    gray: "bg-text-muted",
};

const borderColorClasses: Record<StatusColor, string> = {
    green: "border-quantum-500/20",
    yellow: "border-amber-400/20",
    red: "border-health-500/20",
    gray: "border-border",
};

const bgColorClasses: Record<StatusColor, string> = {
    green: "bg-quantum-500/10",
    yellow: "bg-amber-400/10",
    red: "bg-health-500/10",
    gray: "bg-surface-600",
};

function StatusDot({ status, pulse = false }: { status: StatusColor; pulse?: boolean }) {
    return (
        <div className={`w-2.5 h-2.5 rounded-full ${dotColorClasses[status]} ${
            pulse && status === "green" ? "animate-pulse" : ""
        }`} />
    );
}

function formatNumber(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return n.toString();
}

/** Shown in place of a panel's normal content when its query failed — never
 *  render the zero-filled default markup, which is indistinguishable from a
 *  genuinely idle/healthy system. */
function PanelError({ message }: { message: string }) {
    return (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-health-500/30 bg-health-500/10 text-health-400 text-body-sm">
            <FiAlertCircle />
            <span>{message}</span>
        </div>
    );
}

/** Inline note for HTTP-200-but-partially-failed responses (backend errors[]). */
function PartialDataNote({ sections }: { sections: string[] }) {
    if (sections.length === 0) return null;
    return (
        <p className="text-caption text-amber-400 mb-2 flex items-center gap-1.5">
            <FiAlertCircle className="shrink-0" />
            Partial data — failed to load: {sections.join(", ")}
        </p>
    );
}

// ── Component ───────────────────────────────────────────────────────────────

export default function Dashboard() {
    const { token } = useAuth();

    // Fetch all customers (admin endpoint)
    const {
        data: customers = [],
        isLoading: customersLoading,
        error: customersError,
    } = useQuery<DashboardCustomer[]>({
        queryKey: ["admin-customers"],
        queryFn: async () => {
            if (!token) return [];
            const data = await getAllCustomers(token);
            return Array.isArray(data) ? data : [];
        },
        staleTime: 1000 * 60 * 5,
        enabled: !!token,
    });

    // Monitoring hooks
    const { data: health, isLoading: healthLoading, error: healthError } = useSystemHealth();
    const { data: gridSync, isLoading: gridSyncLoading, error: gridSyncError } = useGridSyncStatus();
    const { data: gridImpacts, isLoading: gridImpactsLoading, error: gridImpactsError } = useGridImpactCoverage();
    const { data: tasks, isLoading: tasksLoading, error: tasksError } = useTaskMonitoring();

    // Compute stats from real data
    const stats = useMemo((): AdminStats => {
        return {
            totalCustomers: customers.length,
            totalProjects: 0,
            activeCustomers: customers.length,
        };
    }, [customers]);

    // Get 5 most recent customers
    const recentCustomers = useMemo(() => {
        return customers
            .sort((a, b) => {
                if (!a.created_at || !b.created_at) return 0;
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            })
            .slice(0, 5);
    }, [customers]);

    // Build customer distribution by plan type
    const planDistribution = useMemo(() => {
        const planCounts: Record<string, number> = {};
        customers.forEach((customer) => {
            const plan = customer.plan_type || 'basic';
            const displayName = plan.charAt(0).toUpperCase() + plan.slice(1);
            planCounts[displayName] = (planCounts[displayName] || 0) + 1;
        });
        return Object.entries(planCounts).map(([name, count]) => ({
            name,
            count,
        }));
    }, [customers]);

    const isLoading = customersLoading;
    const hasError = Boolean(
        customersError || healthError || gridSyncError || gridImpactsError || tasksError
    );

    // Overall status from health endpoint. A failed health *query* (network,
    // 5xx, auth) is a "down" system, not a system that's still "loading" —
    // falling back to "loading" forever would render as a permanent grey dot.
    const overallStatus = healthError
        ? "down"
        : health?.overall_status ?? (healthLoading ? "loading" : "unknown");
    const overallColor = healthLoading ? "gray" as StatusColor : statusColor(overallStatus);

    /* ——— STAT CARD COMPONENT ——— */
    const StatCard = ({
        icon,
        label,
        value,
        isLoading,
    }: {
        icon: JSX.Element;
        label: string;
        value: string | number;
        isLoading?: boolean;
    }) => (
        <div className="card flex items-center gap-4">
            <div className="p-3 rounded-lg bg-quantum-500/10 text-quantum-400 text-2xl">
                {icon}
            </div>
            <div>
                <p className="text-body-sm text-text-secondary">{label}</p>
                <p className="text-metric text-text-primary">
                    {isLoading ? (
                        <span className="inline-block w-8 h-6 bg-surface-600 animate-pulse rounded" />
                    ) : (
                        value
                    )}
                </p>
            </div>
        </div>
    );

    /* ——— LOADING SKELETON ——— */
    const CardSkeleton = () => (
        <div className="card space-y-3">
            <div className="h-5 bg-surface-600 animate-pulse rounded w-1/3" />
            <div className="h-4 bg-surface-600 animate-pulse rounded w-2/3" />
            <div className="h-4 bg-surface-600 animate-pulse rounded w-1/2" />
            <div className="h-4 bg-surface-600 animate-pulse rounded w-3/4" />
        </div>
    );

    return (
        <div className="p-6 space-y-6">
            {/* Page header */}
            <div>
                <h1 className="text-h2 text-text-primary">Admin Dashboard</h1>
                <p className="text-body-sm text-text-secondary mt-1">
                    System overview and customer metrics
                </p>
            </div>

            {/* Error banner */}
            {hasError && (
                <div className="bg-health-500/10 border border-health-500/30 text-health-400 px-4 py-3 rounded-lg flex items-center gap-2">
                    <FiAlertCircle />
                    <span>One or more dashboard sections failed to load — see the affected panels below. Try refreshing the page.</span>
                </div>
            )}

            {/* ——— Top Stats ——— */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <StatCard
                    icon={<FiUsers />}
                    label="Total Customers"
                    value={stats.totalCustomers}
                    isLoading={isLoading}
                />
                <StatCard
                    icon={<FiFolder />}
                    label="Active Customers"
                    value={stats.activeCustomers}
                    isLoading={isLoading}
                />
                <div className="card flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${bgColorClasses[overallColor]} text-2xl`}>
                        <FiServer className={
                            overallColor === "green" ? "text-quantum-400"
                            : overallColor === "yellow" ? "text-amber-400"
                            : overallColor === "red" ? "text-health-400"
                            : "text-text-muted"
                        } />
                    </div>
                    <div>
                        <p className="text-body-sm text-text-secondary">System Status</p>
                        <div className="flex items-center gap-2">
                            <StatusDot status={overallColor} pulse />
                            <p className="text-metric text-text-primary capitalize">
                                {healthLoading ? (
                                    <span className="inline-block w-16 h-6 bg-surface-600 animate-pulse rounded" />
                                ) : overallStatus}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ——— System Health + Worker/Tasks ——— */}
            <div className="grid gap-4 lg:grid-cols-2">
                {/* System Health Card */}
                {healthLoading ? <CardSkeleton /> : (
                    <div className="card">
                        <h3 className="mb-4 flex items-center gap-2 text-h4 text-text-primary">
                            <FiActivity className="text-quantum-400" /> System Health
                        </h3>
                        {healthError ? (
                            <PanelError message="Failed to load system health. The health check itself is unreachable — status below is not current." />
                        ) : (
                        <>
                        <div className="space-y-2">
                            {health?.components.map((c) => {
                                const color = statusColor(c.status);
                                return (
                                    <div
                                        key={c.name}
                                        className={`flex items-center justify-between p-2.5 rounded-lg border ${borderColorClasses[color]} ${bgColorClasses[color]}`}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <StatusDot status={color} pulse />
                                            <span className="text-body-sm font-medium text-text-primary">
                                                {c.name}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 text-caption text-text-secondary">
                                            {c.latency_ms != null && (
                                                <span>{c.latency_ms}ms</span>
                                            )}
                                            {c.detail && (
                                                <span className="max-w-[200px] truncate">{c.detail}</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {health?.checked_at && (
                            <p className="mt-3 text-caption text-text-muted">
                                Checked {timeAgo(health.checked_at)}
                            </p>
                        )}
                        </>
                        )}
                    </div>
                )}

                {/* Worker / Tasks Card */}
                {tasksLoading ? <CardSkeleton /> : (
                    <div className="card">
                        <h3 className="mb-4 flex items-center gap-2 text-h4 text-text-primary">
                            <FiZap className="text-quantum-400" /> Workers & Tasks
                        </h3>
                        {tasksError ? (
                            <PanelError message="Failed to load worker & task status." />
                        ) : (
                        <>
                        <PartialDataNote sections={tasks?.errors ?? []} />
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="text-center p-2 bg-surface-600 rounded-lg">
                                <p className="text-metric text-text-primary">{tasks?.worker_count ?? 0}</p>
                                <p className="text-caption text-text-secondary">Workers</p>
                            </div>
                            <div className="text-center p-2 bg-surface-600 rounded-lg">
                                <p className="text-metric text-text-primary">{tasks?.active_tasks.length ?? 0}</p>
                                <p className="text-caption text-text-secondary">Active</p>
                            </div>
                            <div className="text-center p-2 bg-surface-600 rounded-lg">
                                <p className="text-metric text-text-primary">{tasks?.reserved_count ?? 0}</p>
                                <p className="text-caption text-text-secondary">Queued</p>
                            </div>
                        </div>

                        {/* Active tasks */}
                        {(tasks?.active_tasks.length ?? 0) > 0 && (
                            <div className="mb-3">
                                <p className="text-caption text-text-secondary mb-1.5">Active Tasks</p>
                                {tasks!.active_tasks.map((t) => (
                                    <div key={t.task_id} className="flex items-center gap-2 p-1.5 text-caption">
                                        <StatusDot status="green" pulse />
                                        <span className="text-text-primary truncate">
                                            {t.task_name ?? t.task_id.slice(0, 8)}
                                        </span>
                                        {t.started && (
                                            <span className="text-text-muted ml-auto">{timeAgo(t.started)}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Recent model runs */}
                        <p className="text-caption text-text-secondary mb-1.5">
                            Recent Runs
                            {tasks && (
                                <span className="ml-2 text-text-muted">
                                    (today: {tasks.runs_today}{tasks.failures_today > 0 ? `, ${tasks.failures_today} failed` : ""})
                                </span>
                            )}
                        </p>
                        <div className="space-y-1">
                            {(tasks?.recent_runs ?? []).slice(0, 5).map((r) => {
                                const runColor: StatusColor =
                                    r.status === "completed" ? "green"
                                    : r.status === "running" ? "yellow"
                                    : r.status === "failed" ? "red"
                                    : "gray";
                                return (
                                    <div key={r.run_id} className="flex items-center gap-2 p-1.5 text-caption">
                                        <StatusDot status={runColor} />
                                        <span className="text-text-primary truncate flex-1">
                                            {r.project_name ?? r.run_id.slice(0, 8)}
                                        </span>
                                        <span className="text-text-muted capitalize">{r.status}</span>
                                        <span className="text-text-muted">{timeAgo(r.created_at)}</span>
                                    </div>
                                );
                            })}
                            {(!tasks?.recent_runs || tasks.recent_runs.length === 0) && (
                                <p className="text-caption text-text-muted py-2">No recent runs.</p>
                            )}
                        </div>
                        {tasks?.checked_at && (
                            <p className="mt-3 text-caption text-text-muted">
                                Checked {timeAgo(tasks.checked_at)}
                            </p>
                        )}
                        </>
                        )}
                    </div>
                )}
            </div>

            {/* ——— Grid Sync + Grid Impacts ——— */}
            <div className="grid gap-4 lg:grid-cols-2">
                {/* Grid Sync Card */}
                {gridSyncLoading ? <CardSkeleton /> : (
                    <div className="card">
                        <h3 className="mb-4 flex items-center gap-2 text-h4 text-text-primary">
                            <FiDatabase className="text-quantum-400" /> Grid Sync Pipeline
                        </h3>
                        {gridSyncError ? (
                            <PanelError message="Failed to load grid sync status." />
                        ) : gridSync?.status === "unconfigured" ? (
                            <p className="text-body-sm text-text-muted">Grid data database not configured.</p>
                        ) : (
                            <>
                                <PartialDataNote sections={gridSync?.errors ?? []} />
                                <div className="flex items-center gap-2 mb-3">
                                    <StatusDot status={statusColor(gridSync?.status ?? "unknown")} pulse />
                                    <span className="text-body-sm font-medium text-text-primary capitalize">
                                        {gridSync?.status ?? "unknown"}
                                    </span>
                                    {gridSync?.last_successful_sync && (
                                        <span className="text-caption text-text-muted ml-2">
                                            Last sync: {timeAgo(gridSync.last_successful_sync)}
                                        </span>
                                    )}
                                </div>

                                <div className="grid grid-cols-3 gap-3 mb-4">
                                    <div className="text-center p-2 bg-surface-600 rounded-lg">
                                        <p className="text-metric text-text-primary">{gridSync?.syncs_24h ?? 0}</p>
                                        <p className="text-caption text-text-secondary">Syncs (24h)</p>
                                    </div>
                                    <div className="text-center p-2 bg-surface-600 rounded-lg">
                                        <p className={`text-metric ${(gridSync?.errors_24h ?? 0) > 0 ? "text-health-400" : "text-text-primary"}`}>
                                            {gridSync?.errors_24h ?? 0}
                                        </p>
                                        <p className="text-caption text-text-secondary">Errors (24h)</p>
                                    </div>
                                    <div className="text-center p-2 bg-surface-600 rounded-lg">
                                        <p className="text-metric text-text-primary">
                                            {gridSync?.generation_row_count != null
                                                ? formatNumber(gridSync.generation_row_count)
                                                : "—"}
                                        </p>
                                        <p className="text-caption text-text-secondary">Gen Rows</p>
                                    </div>
                                </div>

                                {/* Recent syncs */}
                                {(gridSync?.recent_syncs.length ?? 0) > 0 && (
                                    <>
                                        <p className="text-caption text-text-secondary mb-1.5">Recent Syncs</p>
                                        <div className="space-y-1 mb-3">
                                            {gridSync!.recent_syncs.slice(0, 5).map((s, i) => (
                                                <div key={i} className="flex items-center gap-2 p-1.5 text-caption">
                                                    <StatusDot status={s.success ? "green" : "red"} />
                                                    <span className="text-text-muted">{timeAgo(s.sync_time)}</span>
                                                    {s.records_inserted != null && (
                                                        <span className="text-text-secondary">
                                                            +{s.records_inserted} ins
                                                        </span>
                                                    )}
                                                    {(s.respondents_failed ?? 0) > 0 && (
                                                        <span className="text-amber-400">
                                                            {s.respondents_failed} respondent{s.respondents_failed === 1 ? "" : "s"} failed
                                                        </span>
                                                    )}
                                                    {s.error_message && (
                                                        <span className="text-health-400 truncate max-w-[200px]">
                                                            {s.error_message}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {/* Stalest regions */}
                                {(gridSync?.stalest_regions.length ?? 0) > 0 && (
                                    <>
                                        <p className="text-caption text-text-secondary mb-1.5">Stalest Regions</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {gridSync!.stalest_regions.slice(0, 8).map((r) => (
                                                <span
                                                    key={r.ba_code}
                                                    className={`text-caption px-2 py-0.5 rounded ${
                                                        (r.hours_stale ?? 0) > 6
                                                            ? "bg-health-500/10 text-health-400"
                                                            : (r.hours_stale ?? 0) > 3
                                                            ? "bg-amber-400/10 text-amber-400"
                                                            : "bg-surface-600 text-text-secondary"
                                                    }`}
                                                >
                                                    {r.ba_code} ({r.hours_stale != null ? `${r.hours_stale}h` : "?"})
                                                </span>
                                            ))}
                                        </div>
                                    </>
                                )}

                                {/* Failing respondents (F6 / Phase 2): failing right now,
                                    N consecutive runs — distinct from merely-stale data above. */}
                                {(gridSync?.failing_respondents.length ?? 0) > 0 && (
                                    <>
                                        <p className="mt-3 text-caption text-text-secondary mb-1.5">
                                            Failing Respondents
                                        </p>
                                        <div className="space-y-1">
                                            {gridSync!.failing_respondents.slice(0, 8).map((f) => (
                                                <div
                                                    key={`${f.data_source}/${f.data_stream}/${f.respondent_code}`}
                                                    className="flex items-center gap-2 p-1.5 text-caption"
                                                >
                                                    <StatusDot status="red" />
                                                    <span className="text-text-primary font-medium">
                                                        {f.respondent_code}
                                                    </span>
                                                    <span className="text-text-muted">
                                                        ({f.data_source}/{f.data_stream})
                                                    </span>
                                                    <span className="text-health-400">
                                                        {f.consecutive_failures} runs in a row
                                                    </span>
                                                    {f.failing_since && (
                                                        <span className="text-text-muted">
                                                            since {timeAgo(f.failing_since)}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        {!gridSyncError && gridSync?.checked_at && (
                            <p className="mt-3 text-caption text-text-muted">
                                Checked {timeAgo(gridSync.checked_at)}
                            </p>
                        )}
                    </div>
                )}

                {/* Grid Impacts Card */}
                {gridImpactsLoading ? <CardSkeleton /> : (
                    <div className="card">
                        <h3 className="mb-4 flex items-center gap-2 text-h4 text-text-primary">
                            <FiCpu className="text-quantum-400" /> Grid Impact Coverage
                        </h3>
                        {gridImpactsError ? (
                            <PanelError message="Failed to load grid impact coverage." />
                        ) : (
                        <>
                        <PartialDataNote sections={gridImpacts?.errors ?? []} />
                        <div className="flex items-center gap-2 mb-3">
                            <StatusDot status={statusColor(gridImpacts?.status ?? "unknown")} pulse />
                            <span className="text-body-sm font-medium text-text-primary capitalize">
                                {gridImpacts?.status ?? "unknown"}
                            </span>
                            {gridImpacts?.latest_computed_at && (
                                <span className="text-caption text-text-muted ml-2">
                                    Updated {timeAgo(gridImpacts.latest_computed_at)}
                                </span>
                            )}
                        </div>

                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="text-center p-2 bg-surface-600 rounded-lg">
                                <p className="text-metric text-text-primary">
                                    {formatNumber(gridImpacts?.total_records ?? 0)}
                                </p>
                                <p className="text-caption text-text-secondary">Records</p>
                            </div>
                            <div className="text-center p-2 bg-surface-600 rounded-lg">
                                <p className="text-metric text-text-primary">{gridImpacts?.distinct_bas ?? 0}</p>
                                <p className="text-caption text-text-secondary">BAs</p>
                            </div>
                            <div className="text-center p-2 bg-surface-600 rounded-lg">
                                <p className="text-metric text-text-primary">
                                    {gridImpacts?.ba_coverage && gridImpacts.ba_coverage.length > 0
                                        ? `${(gridImpacts.ba_coverage.reduce(
                                              (sum, b) => sum + (b.completeness_pct ?? 0), 0
                                          ) / gridImpacts.ba_coverage.length).toFixed(1)}%`
                                        : "—"}
                                </p>
                                <p className="text-caption text-text-secondary">Avg Coverage</p>
                            </div>
                        </div>

                        {/* Time range */}
                        {gridImpacts?.earliest_hour && gridImpacts?.latest_hour && (
                            <div className="text-caption text-text-secondary mb-3 p-2 bg-surface-600 rounded">
                                Range: {new Date(gridImpacts.earliest_hour).toLocaleDateString()} — {new Date(gridImpacts.latest_hour).toLocaleDateString()}
                            </div>
                        )}

                        {/* BA coverage list */}
                        {(gridImpacts?.ba_coverage.length ?? 0) > 0 && (
                            <>
                                <p className="text-caption text-text-secondary mb-1.5">BA Coverage</p>
                                <div className="max-h-32 overflow-y-auto space-y-1 mb-3">
                                    {gridImpacts!.ba_coverage.map((b) => (
                                        <div key={b.ba_code} className="flex items-center justify-between text-caption px-1.5 py-1">
                                            <span className="text-text-primary font-mono">{b.ba_code}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-text-secondary">{formatNumber(b.hours_with_data)} hrs</span>
                                                <span className={
                                                    (b.completeness_pct ?? 0) >= 95 ? "text-quantum-400"
                                                    : (b.completeness_pct ?? 0) >= 80 ? "text-amber-400"
                                                    : "text-health-400"
                                                }>
                                                    {b.completeness_pct != null ? `${b.completeness_pct}%` : "—"}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}

                        {/* Recent gaps */}
                        {(gridImpacts?.recent_gaps.length ?? 0) > 0 && (
                            <>
                                <p className="text-caption text-text-secondary mb-1.5">
                                    Recent Gaps ({gridImpacts!.recent_gaps.length})
                                </p>
                                <div className="space-y-1">
                                    {gridImpacts!.recent_gaps.slice(0, 3).map((g, i) => (
                                        <div key={i} className="flex items-center gap-2 text-caption">
                                            <StatusDot status="red" />
                                            <span className="text-text-muted">
                                                {new Date(g.hour).toLocaleString(undefined, {
                                                    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                                                })}
                                            </span>
                                            <span className="text-health-400">
                                                {g.missing_bas.length} BA{g.missing_bas.length > 1 ? "s" : ""} missing
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                        {gridImpacts?.checked_at && (
                            <p className="mt-3 text-caption text-text-muted">
                                Checked {timeAgo(gridImpacts.checked_at)}
                            </p>
                        )}
                        </>
                        )}
                    </div>
                )}
            </div>

            {/* ——— Sync Heatmap (full width) ——— */}
            <div className="card">
                <h3 className="mb-4 flex items-center gap-2 text-h4 text-text-primary">
                    <FiDatabase className="text-quantum-400" /> Grid Data Coverage Heatmap
                </h3>
                <p className="text-caption text-text-secondary mb-3">
                    Generation data coverage by region — yearly, monthly, and daily resolution
                </p>
                <SyncHeatmap />
            </div>

            {/* ——— Grid: Recent Customers · Plan Distribution ——— */}
            <div className="grid gap-4 lg:grid-cols-3">
                {/* —— Recent Customers —— */}
                <div className="card lg:col-span-1">
                    <h3 className="mb-4 flex items-center gap-2 text-h4 text-text-primary">
                        <FiClock className="text-quantum-400" /> Recent Customers
                    </h3>
                    {isLoading ? (
                        <div className="space-y-3">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="h-5 bg-surface-600 animate-pulse rounded w-3/4" />
                            ))}
                        </div>
                    ) : recentCustomers.length === 0 ? (
                        <p className="text-body-sm text-text-muted">No customers yet.</p>
                    ) : (
                        <ul className="space-y-3 text-body-sm">
                            {recentCustomers.map(c => (
                                <li key={c.customer_id} className="flex justify-between items-center">
                                    <Link
                                        to={`/admin/clients/${encodeURIComponent(c.customer_id)}`}
                                        className="text-quantum-400 hover:text-quantum-300 hover:underline"
                                    >
                                        {c.customer_name}
                                    </Link>
                                    <span className="text-caption text-text-muted bg-surface-600 px-2 py-0.5 rounded">
                                        {c.plan_type || 'basic'}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="mt-4 pt-3 border-t border-border-subtle">
                        <Link
                            to="/admin/clients"
                            className="text-body-sm text-quantum-400 hover:text-quantum-300 hover:underline"
                        >
                            View all customers →
                        </Link>
                    </div>
                </div>

                {/* —— Plan Distribution Chart —— */}
                <div className="card lg:col-span-2">
                    <h3 className="mb-4 flex items-center gap-2 text-h4 text-text-primary">
                        <FiCpu className="text-quantum-400" /> Customer Plan Distribution
                    </h3>
                    {isLoading ? (
                        <div className="h-64 bg-surface-600 animate-pulse rounded" />
                    ) : planDistribution.length === 0 ? (
                        <p className="text-body-sm text-text-muted text-center py-8">
                            No customer data available.
                        </p>
                    ) : (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={planDistribution}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                                    <YAxis allowDecimals={false} stroke="#94a3b8" fontSize={12} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#0f1d30',
                                            border: '1px solid #1e3a5f',
                                            borderRadius: '8px',
                                            color: '#f4f4f5'
                                        }}
                                    />
                                    <Bar dataKey="count" fill="#009639" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </div>

            {/* —— Quick Actions —— */}
            <div className="card">
                <h3 className="mb-4 flex items-center gap-2 text-h4 text-text-primary">
                    <FiCalendar className="text-quantum-400" /> Quick Actions
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <Link
                        to="/admin/clients"
                        className="rounded-lg border border-border bg-surface-600 p-4 hover:bg-surface-500 transition-colors"
                    >
                        <h4 className="font-medium text-text-primary">Manage Customers</h4>
                        <p className="mt-1 text-body-sm text-text-secondary">
                            View and manage all customer accounts.
                        </p>
                    </Link>

                    <Link
                        to="/admin/model"
                        className="rounded-lg border border-border bg-surface-600 p-4 hover:bg-surface-500 transition-colors"
                    >
                        <h4 className="font-medium text-text-primary">Run Model</h4>
                        <p className="mt-1 text-body-sm text-text-secondary">
                            Execute LCA model for testing.
                        </p>
                    </Link>

                    <Link
                        to="/admin/users"
                        className="rounded-lg border border-border bg-surface-600 p-4 hover:bg-surface-500 transition-colors"
                    >
                        <h4 className="font-medium text-text-primary">Manage Users</h4>
                        <p className="mt-1 text-body-sm text-text-secondary">
                            View and manage user accounts.
                        </p>
                    </Link>
                </div>
            </div>
        </div>
    );
}
